import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Card,
  CardContent,
  Pagination,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  Divider,
  Slider,
  Checkbox,
  FormControlLabel,
  alpha,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Sort as SortIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  AccessTime as TimeIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import SEOMetaTags from '../components/SEO/SEOMetaTags';
import DoctorCard from '../components/HomePage/DoctorCard';
import { fetchDoctors } from '../features/doctors/doctorsSlice';
import {
  selectDoctors,
  selectDoctorsLoading,
  selectDoctorsError,
  selectDoctorsCount,
} from '../features/doctors/doctorsSlice';

/**
 * Doctors Listing Page - Marketplace view for browsing all doctors
 * SEO optimized with filters, search, and sorting
 */
const DoctorsListing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const doctors = useSelector(selectDoctors);
  const loading = useSelector(selectDoctorsLoading);
  const error = useSelector(selectDoctorsError);
  const totalCount = useSelector(selectDoctorsCount);

  // Local state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    location: searchParams.get('location') || searchParams.get('city') || '',
    speciality: searchParams.get('speciality') || '',
    language: searchParams.get('language') || '',
    consultationType: searchParams.get('consultation_type') || 'all',
    minFee: searchParams.get('min_fee') || '',
    maxFee: searchParams.get('max_fee') || '',
    minRating: searchParams.get('min_rating') || '',
    verifiedOnly: searchParams.get('verified') === 'true',
  });
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Debounced search function
  const debouncedFetchDoctors = useMemo(
    () => debounce(async (params) => {
      await dispatch(fetchDoctors(params));
    }, 500),
    [dispatch]
  );

  // Fetch doctors when filters change (with debouncing for search)
  useEffect(() => {
    const params = {
      ...filters,
      sortBy,
      page,
      page_size: 12,
    };

    // Use debounced function for search fields, immediate for others
    if (filters.search || filters.location) {
      debouncedFetchDoctors(params);
    } else {
      dispatch(fetchDoctors(params));
    }

    // Cleanup
    return () => {
      debouncedFetchDoctors.cancel();
    };
  }, [dispatch, filters, sortBy, page, debouncedFetchDoctors]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        params.append(key, value);
      }
    });
    if (sortBy !== 'newest') params.append('sort', sortBy);
    if (page > 1) params.append('page', page);
    
    navigate(`/doctors?${params.toString()}`, { replace: true });
  }, [filters, sortBy, page, navigate]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      location: '',
      speciality: '',
      language: '',
      consultationType: 'all',
      minFee: '',
      maxFee: '',
      minRating: '',
      verifiedOnly: false,
    });
    setSortBy('newest');
    setPage(1);
  };

  const activeFiltersCount = Object.values(filters).filter(
    (v) => v && v !== 'all' && v !== '' && v !== false
  ).length;

  // SEO structured data
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Mental Health Professionals Directory',
    description: 'Browse verified psychiatrists, psychologists, therapists, and counselors',
    numberOfItems: totalCount,
    itemListElement: doctors.slice(0, 10).map((doctor, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Physician',
        name: `Dr. ${doctor.name}`,
        medicalSpecialty: doctor.specialities?.map((s) => s.name) || [],
      },
    })),
  };

  return (
    <>
      <SEOMetaTags
        title="Find Mental Health Professionals - Browse Verified Doctors"
        description="Browse and search through verified psychiatrists, psychologists, therapists, and counselors. Filter by specialization, location, consultation type, and more."
        keywords={['find psychiatrist', 'find psychologist', 'find therapist', 'mental health professional', 'doctor directory', 'psychiatrist directory']}
        structuredData={structuredData}
        url="/doctors"
      />

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 4 } }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
              Find Mental Health Professionals
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {totalCount > 0 ? `${totalCount} verified professionals available` : 'Searching for professionals...'}
            </Typography>
          </Box>

          {/* Search and Filters Bar */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 4 }}
          >
            <TextField
              fullWidth
              placeholder="Search by name, specialization, or keywords..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 2 }}
            />
            <TextField
              fullWidth
              placeholder="Location (city, state)"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: filters.location && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={() => handleFilterChange('location', '')}
                      aria-label="Clear location"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
                startAdornment={<SortIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="experience">Most Experienced</MenuItem>
                <MenuItem value="fee">Price: Low to High</MenuItem>
                <MenuItem value="fee_desc">Price: High to Low</MenuItem>
              </Select>
            </FormControl>
            {isMobile && (
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDrawerOpen(true)}
                sx={{ minWidth: 120 }}
              >
                Filters
                {activeFiltersCount > 0 && (
                  <Chip
                    label={activeFiltersCount}
                    size="small"
                    sx={{ ml: 1, height: 20, minWidth: 20 }}
                  />
                )}
              </Button>
            )}
          </Stack>

          <Grid container spacing={3}>
            {/* Sidebar Filters (Desktop) */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <Card sx={{ p: 2, position: 'sticky', top: 20 }}>
                  <Stack spacing={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" fontWeight={600}>
                        Filters
                      </Typography>
                      {activeFiltersCount > 0 && (
                        <Button size="small" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                          Clear
                        </Button>
                      )}
                    </Box>

                    <Divider />

                    {/* Speciality Filter */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Specialization
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        select
                        value={filters.speciality}
                        onChange={(e) => handleFilterChange('speciality', e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        <option value="">All Specializations</option>
                        <option value="Psychiatrist">Psychiatrist</option>
                        <option value="Psychologist">Psychologist</option>
                        <option value="Therapist">Therapist</option>
                        <option value="Counselor">Counselor</option>
                        <option value="Clinical Psychologist">Clinical Psychologist</option>
                      </TextField>
                    </Box>

                    {/* Consultation Type */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Consultation Type
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={filters.consultationType}
                          onChange={(e) => handleFilterChange('consultationType', e.target.value)}
                        >
                          <MenuItem value="all">All Types</MenuItem>
                          <MenuItem value="video">Video Call</MenuItem>
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="in-person">In-Person</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Verified Only */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.verifiedOnly}
                          onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VerifiedIcon fontSize="small" color="primary" />
                          <Typography variant="body2">Verified Only</Typography>
                        </Box>
                      }
                    />

                    {/* Price Range */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Consultation Fee
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          placeholder="Min"
                          type="number"
                          value={filters.minFee}
                          onChange={(e) => handleFilterChange('minFee', e.target.value)}
                          InputProps={{ startAdornment: <Typography variant="caption">₹</Typography> }}
                        />
                        <TextField
                          size="small"
                          placeholder="Max"
                          type="number"
                          value={filters.maxFee}
                          onChange={(e) => handleFilterChange('maxFee', e.target.value)}
                          InputProps={{ startAdornment: <Typography variant="caption">₹</Typography> }}
                        />
                      </Stack>
                    </Box>

                    {/* Rating Filter */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Minimum Rating
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Any rating"
                        value={filters.minRating}
                        onChange={(e) => handleFilterChange('minRating', e.target.value)}
                        inputProps={{ min: 1, max: 5, step: 0.5 }}
                      />
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            )}

            {/* Doctors Grid */}
            <Grid item xs={12} md={9}>
              {(() => {
                if (loading) {
                  return (
                    <Grid container spacing={3}>
                      {[...Array(6)].map((_, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                          <Card>
                            <Box sx={{ height: 200, bgcolor: 'grey.200' }} />
                            <CardContent>
                              <Typography variant="h6">
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Loading...
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  );
                }
                if (error) {
                  return (
                    <Alert severity="error">
                      Failed to load doctors. Please try again later.
                    </Alert>
                  );
                }
                if (doctors.length > 0) {
                  return (
                    <>
                      <Grid container spacing={3} sx={{ mb: 4 }}>
                        <AnimatePresence>
                          {doctors.map((doctor, index) => (
                            <Grid item xs={12} sm={6} lg={4} key={doctor.id}>
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ delay: index * 0.05 }}
                              >
                                <DoctorCard 
                                  doctor={doctor} 
                                  onSelect={(doctor) => navigate(`/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`)}
                                  onBookAppointment={(doctor) => navigate(`/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`)}
                                />
                              </motion.div>
                            </Grid>
                          ))}
                        </AnimatePresence>
                      </Grid>

                      {/* Pagination */}
                      {totalCount > 12 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                          <Pagination
                            count={Math.ceil(totalCount / 12)}
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                            size="large"
                          />
                        </Box>
                      )}
                    </>
                  );
                }
                return (
                  <Alert severity="info">
                    No doctors found matching your criteria. Try adjusting your filters.
                  </Alert>
                );
              })()}
            </Grid>
          </Grid>

          {/* Mobile Filter Drawer */}
          <Drawer
            anchor="bottom"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            PaperProps={{
              sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Filters
                </Typography>
                <IconButton onClick={() => setFilterDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {/* Mobile filters - same as desktop sidebar */}
              <Stack spacing={3}>
                {/* Speciality Filter */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Specialization
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    value={filters.speciality}
                    onChange={(e) => handleFilterChange('speciality', e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All Specializations</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Psychologist">Psychologist</option>
                    <option value="Therapist">Therapist</option>
                    <option value="Counselor">Counselor</option>
                    <option value="Clinical Psychologist">Clinical Psychologist</option>
                  </TextField>
                </Box>

                {/* Consultation Type */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Consultation Type
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={filters.consultationType}
                      onChange={(e) => handleFilterChange('consultationType', e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="video">Video Call</MenuItem>
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="in-person">In-Person</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Verified Only */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.verifiedOnly}
                      onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VerifiedIcon fontSize="small" color="primary" />
                      <Typography variant="body2">Verified Only</Typography>
                    </Box>
                  }
                />

                {/* Price Range */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Consultation Fee
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={filters.minFee}
                      onChange={(e) => handleFilterChange('minFee', e.target.value)}
                      InputProps={{ startAdornment: <Typography variant="caption">₹</Typography> }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={filters.maxFee}
                      onChange={(e) => handleFilterChange('maxFee', e.target.value)}
                      InputProps={{ startAdornment: <Typography variant="caption">₹</Typography> }}
                    />
                  </Stack>
                </Box>

                {/* Rating Filter */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Minimum Rating
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Any rating"
                    value={filters.minRating}
                    onChange={(e) => handleFilterChange('minRating', e.target.value)}
                    inputProps={{ min: 1, max: 5, step: 0.5 }}
                  />
                </Box>

                {/* Clear Filters Button */}
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear All Filters
                  </Button>
                )}
              </Stack>
            </Box>
          </Drawer>
        </Container>
      </Box>
    </>
  );
};

export default DoctorsListing;

